import { useTranslation } from 'react-i18next';
import { CSSProperties } from 'react';

import {
  ProfilePhotos,
  UserPhotosProps
} from '@/components/ProfileData/ProfilePhotos/ProfilePhotos.tsx';
import { StepTitle } from '@/components/common/Steps/Steps.tsx';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import UserCheckShape from '@/components/common/svg/shapes/UserCheckShape.tsx';
import UserRejectShape from '@/components/common/svg/shapes/UserRejectShape.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { Gender } from '@/enums/apiEnums.ts';
import HeartUnlockShape from '@/components/common/svg/shapes/HeartUnlockShape.tsx';
import ProfileVideoBlock from '@/components/ProfileData/ProfileVideo/ProfileVideoBlock.tsx';

export function UserPhotos({
  photos,
  updateFields,
  isSaving,
  setIsSaving,
  isVideoUploading,
  onVideoUploadingChange
}: UserPhotosProps & {
  isVideoUploading?: boolean;
  onVideoUploadingChange?: (uploading: boolean) => void;
}) {
  const { t } = useTranslation();
  const { aboutMe } = useProfileStore();
  const isMale = aboutMe?.profile?.gender === Gender.MALE;
  const isFemale = aboutMe?.profile?.gender === Gender.FEMALE;
  return (
    <div style={containerStyle}>
      <StepTitle
        title={t('registration.add_photos')}
        subTitle={t('photos.add_one_photo')}
      />
      <ProfilePhotos
        photos={photos}
        updateFields={updateFields}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        isVideoUploading={isVideoUploading}
      />

      {isFemale && (
        <InputContent style={{ marginTop: 24 }}>
          <div style={{ ...optionStyle, padding: 0, borderBottom: 'none' }}>
            <div
              style={{
                ...iconWrapperStyle,
                background: 'linear-gradient(90deg, #8681FA 0%, #D16FD9 100%)',
                borderRadius: '50%'
              }}
            >
              <HeartUnlockShape width={25} height={25} />
            </div>
            <div style={textContainerStyle}>
              <span style={mainTextStyle}>{t('video.unlock')}</span>
              <span style={subTextStyle}>{t('video.unlock_sub')}</span>
            </div>
          </div>
        </InputContent>
      )}

      <InputContent style={{ marginTop: 24 }}>
        <ProfileVideoBlock
          showBadge={isFemale}
          isAnyPhotoUploading={isSaving?.some((s) => s)}
          onUploadingChange={onVideoUploadingChange}
        />
      </InputContent>

      {isMale && (
        <InputContent style={{ marginTop: 24 }}>
          <div style={cardStyle}>
            <div style={optionStyle}>
              <div style={iconWrapperStyle}>
                <UserCheckShape width={25} height={25} />
              </div>
              <div style={textContainerStyle}>
                <span style={mainTextStyle}>
                  {t('photos.only_real_photos')}
                </span>
                <span style={subTextStyle}>{t('photos.no_fake_photos')}</span>
              </div>
            </div>
            <div style={{ ...optionStyle, borderBottom: 'none' }}>
              <div style={iconWrapperStyle}>
                <UserRejectShape width={25} height={25} />
              </div>
              <div style={textContainerStyle}>
                <span style={mainTextStyle}>{t('photos.no_low_quality')}</span>
                <span style={subTextStyle}>{t('photos.right_to_reject')}</span>
              </div>
            </div>
          </div>
        </InputContent>
      )}
    </div>
  );
}

const containerStyle: CSSProperties = {
  textAlign: 'center'
};

export const cardStyle: CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
  overflow: 'hidden'
};

export const optionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '14px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.04)'
};

export const iconWrapperStyle: CSSProperties = {
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #F5F5F5 0%, #EBEBEB 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginRight: '12px',
  boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.8)'
};

export const textContainerStyle: CSSProperties = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  gap: '2px'
};

export const mainTextStyle: CSSProperties = {
  fontSize: '12px',
  fontWeight: 590,
  color: '#1A1A1A',
  lineHeight: '1.3'
};

export const subTextStyle: CSSProperties = {
  fontSize: '11px',
  color: '#8B8B8E',
  fontWeight: 510,
  lineHeight: '1.3'
};
